import { useState } from "react";
import { Box, Typography, IconButton } from "@mui/material";
import DeleteIcon from "@mui/icons-material/Delete";
import AddIcon from "@mui/icons-material/Add";
import AutoAwesomeIcon from "@mui/icons-material/AutoAwesome";
import TextField from "./TextField";
import Loader from "../Loaders/Loader";
import ButtonMenu from "../Buttons/ButtonMenu";
import { useController } from "react-hook-form";
import {
  BRAND_PLACEHOLDER,
  CURRENT_PRICE_PLACEHOLDER,
  CUSTOM_LABEL_NUMBER_PLACEHOLDER,
  CUSTOM_NUMBER_PLACEHOLDER,
  DESCRIPTION_PLACEHOLDER,
  NAME_PLACEHOLDER,
  PRICE_PLACEHOLDER,
  RETAILER_ID_PLACEHOLDER,
  SHORT_DESCRIPTION_PLACEHOLDER,
  UNIT_PRICE_PLACEHOLDER,
  URL_PLACEHOLDER,
} from "../../i18n/keysTranslations";

const TextFieldWithActions = ({
  name,
  label,
  labelTitle,
  disabled,
  readOnly = false,
  control,
  errors,
  rules,
  sx,
  showAddIcon = true,
  showDeleteIcon = true,
  onChange = () => {},
  onAdd = () => {},
  onRemove = () => {},
  onGenerate = () => {},
  type = "titles",
  maxLength = 0,
  showCounterCharacters = true,
  index,
  firstItemMaxLength = 0,
  platform,
  isCatalogMeta = false,
}) => {
  const [isGenerating, setIsGenerating] = useState(false);

  const {
    field: { value: valueField, onChange: onChangeField },
  } = useController({
    control,
    name,
  });

  const getRows = () => {
    if (platform === "meta") {
      if (type === "headlines" || type === "destinationUrl") {
        return { min: 1, max: 2 };
      }
      return { min: 3, max: 3 };
    }

    if (platform === "tiktok") {
      if (type === "titles") {
        return { min: 2, max: 2 };
      }
      return { min: 3, max: 3 };
    }

    if (platform === "google") {
      if (type === "titles") {
        return { min: 1, max: 1 };
      }
      return { min: 3, max: 3 };
    }

    if (platform === "linkedIn") {
      if (type === "titles") {
        return { min: 1, max: 2 };
      }
      return { min: 3, max: 3 };
    }

    return { min: 3, max: 3 };
  };

  const rows = getRows();

  const isDisabled = disabled || isGenerating;
  const showGenerateIcon = type !== "destinationUrl";

  const onAddExtrapolation = (value) => {
    onChangeField(`${valueField} ${value}`);
  };

  const buttonMenuOptions = [
    {
      label: NAME_PLACEHOLDER,
      value: "{{product.name}}",
      onClick: () => onAddExtrapolation("{{product.name}}"),
    },
    {
      label: BRAND_PLACEHOLDER,
      value: "{{product.brand}}",
      onClick: () => onAddExtrapolation("{{product.brand}}"),
    },
    {
      label: RETAILER_ID_PLACEHOLDER,
      value: "{{product.retailer_id}}",
      onClick: () => onAddExtrapolation("{{product.retailer_id}}"),
    },
    {
      label: DESCRIPTION_PLACEHOLDER,
      value: "{{product.description}}",
      onClick: () => onAddExtrapolation("{{product.description}}"),
    },
    {
      label: SHORT_DESCRIPTION_PLACEHOLDER,
      value: "{{product.short_description}}",
      onClick: () => onAddExtrapolation("{{product.short_description}}"),
    },
    {
      label: PRICE_PLACEHOLDER,
      value: "{{product.price}}",
      onClick: () => onAddExtrapolation("{{product.price}}"),
    },
    {
      label: CURRENT_PRICE_PLACEHOLDER,
      value: "{{product.current_price}}",
      onClick: () => onAddExtrapolation("{{product.current_price}}"),
    },
    {
      label: UNIT_PRICE_PLACEHOLDER,
      value: "{{product.unit_price}}",
      onClick: () => onAddExtrapolation("{{product.unit_price}}"),
    },
    {
      label: CUSTOM_LABEL_NUMBER_PLACEHOLDER,
      extrapolation: { value: 0 },
      value: "{{product.custom_label_0}}",
      onClick: () => onAddExtrapolation("{{product.custom_label_0}}"),
    },
    {
      label: CUSTOM_LABEL_NUMBER_PLACEHOLDER,
      extrapolation: { value: 1 },
      value: "{{product.custom_label_1}}",
      onClick: () => onAddExtrapolation("{{product.custom_label_1}}"),
    },
    {
      label: CUSTOM_LABEL_NUMBER_PLACEHOLDER,
      extrapolation: { value: 2 },
      value: "{{product.custom_label_2}}",
      onClick: () => onAddExtrapolation("{{product.custom_label_2}}"),
    },
    {
      label: CUSTOM_LABEL_NUMBER_PLACEHOLDER,
      extrapolation: { value: 3 },
      value: "{{product.custom_label_3}}",
      onClick: () => onAddExtrapolation("{{product.custom_label_3}}"),
    },
    {
      label: CUSTOM_LABEL_NUMBER_PLACEHOLDER,
      extrapolation: { value: 4 },
      value: "{{product.custom_label_4}}",
      onClick: () => onAddExtrapolation("{{product.custom_label_4}}"),
    },
    {
      label: CUSTOM_NUMBER_PLACEHOLDER,
      extrapolation: { value: 0 },
      value: "{{product.custom_number_0}}",
      onClick: () => onAddExtrapolation("{{product.custom_number_0}}"),
    },
    {
      label: CUSTOM_NUMBER_PLACEHOLDER,
      extrapolation: { value: 1 },
      value: "{{product.custom_number_1}}",
      onClick: () => onAddExtrapolation("{{product.custom_number_1}}"),
    },
    {
      label: CUSTOM_NUMBER_PLACEHOLDER,
      extrapolation: { value: 2 },
      value: "{{product.custom_number_2}}",
      onClick: () => onAddExtrapolation("{{product.custom_number_2}}"),
    },
    {
      label: CUSTOM_NUMBER_PLACEHOLDER,
      extrapolation: { value: 3 },
      value: "{{product.custom_number_3}}",
      onClick: () => onAddExtrapolation("{{product.custom_number_3}}"),
    },
    {
      label: CUSTOM_NUMBER_PLACEHOLDER,
      extrapolation: { value: 4 },
      value: "{{product.custom_number_4}}",
      onClick: () => onAddExtrapolation("{{product.custom_number_4}}"),
    },
    {
      label: URL_PLACEHOLDER,
      value: "{{product.url}}",
      onClick: () => onAddExtrapolation("{{product.url}}"),
    },
  ];

  const maxCharacters =
    index === 0 && firstItemMaxLength ? firstItemMaxLength : maxLength;

  return (
    <Box sx={{ display: "flex", flexDirection: "column", gap: 1 }}>
      <Box
        sx={{
          display: "flex",
          flexDirection: "row",
          alignItems: "center",
          justifyContent: labelTitle ? "space-between" : "flex-end",
          gap: 0.5,
        }}
      >
        {labelTitle && <Typography variant="body1">{labelTitle}</Typography>}

        <Box sx={{ display: "flex", alignItems: "center" }}>
          {showDeleteIcon && (
            <IconButton
              disabled={isDisabled}
              size="small"
              onClick={onRemove}
              sx={{ p: 0.4, zIndex: 1 }}
            >
              <DeleteIcon />
            </IconButton>
          )}

          {showGenerateIcon &&
            (isGenerating ? (
              <Loader size={20} hasMarginTop={false} />
            ) : (
              <IconButton
                disabled={isDisabled}
                size="small"
                onClick={async () => {
                  setIsGenerating(true);
                  try {
                    await onGenerate();
                  } finally {
                    setIsGenerating(false);
                  }
                }}
                sx={{ p: 0.4, zIndex: 1 }}
              >
                <AutoAwesomeIcon />
              </IconButton>
            ))}

          {showAddIcon && isCatalogMeta ? (
            <ButtonMenu
              mode="icon"
              iconButton={<AddIcon />}
              options={buttonMenuOptions}
              sx={{
                zIndex: 1,
                p: 0.4,
                mr: showGenerateIcon ? -1.5 : -0.5,
              }}
            />
          ) : showAddIcon ? (
            <IconButton
              disabled={isDisabled}
              size="small"
              onClick={onAdd}
              sx={{ p: 0.4, zIndex: 1 }}
            >
              <AddIcon />
            </IconButton>
          ) : null}
        </Box>
      </Box>

      <Box
        sx={{
          display: "flex",
          alignItems: "center",
          position: "relative",
          width: "100%",
          mb: 1,
        }}
      >
        <TextField
          label={label}
          name={name}
          variant="filled"
          multiline
          showCounterCharacters={showCounterCharacters}
          maxCharacters={maxCharacters}
          minRows={rows.min}
          maxRows={rows.max}
          fullWidth
          disabled={disabled}
          actionsChange={onChange}
          control={control}
          errors={errors}
          rules={rules}
          InputProps={readOnly ? { readOnly: true } : {}}
          sx={{
            ...sx,
            "& .MuiInputBase-inputMultiline": {
              resize: "vertical",
              overflowY: "auto",
              pb: 4,
            },
            zIndex: 1,
            position: "relative",
          }}
        />
      </Box>
    </Box>
  );
};

export default TextFieldWithActions;
